import React from "react";
import { Instagram, Mail, ArrowRight } from "lucide-react";
import { RedWave } from "@/components/RedWave";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_d2f1a296-f421-4ca2-ae61-9d54acaa8e8a/artifacts/em50v0eb_Logo.png";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative pt-24 md:pt-36 pb-12 bg-black border-t border-[color:var(--fs-hairline)] overflow-hidden"
    >
      {/* Big closing CTA */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
        <span className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--fs-gold-muted)]">
          09 / The Invitation
        </span>
        <h3
          className="mt-6 font-display font-light text-5xl sm:text-7xl lg:text-[9rem] leading-[0.9] text-white"
          data-testid="footer-headline"
        >
          Enter the
          <br />
          <span className="italic text-gold-foil">Flow State.</span>
        </h3>

        <div className="mx-auto mt-10 w-64 max-w-full opacity-95">
          <RedWave className="w-full h-10" strokeWidth={2} />
        </div>

        <div className="mt-10">
          <a
            href="#product"
            className="btn-gold"
            data-testid="footer-cta"
          >
            Shop the Blend
            <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-[color:var(--fs-hairline)]">
          <div className="md:col-span-5 flex items-start gap-4">
            <img
              src={LOGO_URL}
              alt="Flow State Coffee"
              className="h-14 w-14 object-contain"
            />
            <div>
              <div className="font-display text-xl tracking-wider text-gold-foil uppercase">
                Flow State
              </div>
              <p className="mt-3 text-sm text-neutral-500 max-w-xs leading-relaxed">
                Premium blend coffee engineered for calm energy and
                deep focus.
              </p>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--fs-gold-muted)] mb-4">
              Explore
            </div>
            <ul className="space-y-3 text-sm text-neutral-400">
              <li>
                <a href="#difference" className="link-wave" data-testid="footer-link-difference">
                  The Difference
                </a>
              </li>
              <li>
                <a href="#credibility" className="link-wave" data-testid="footer-link-coffee">
                  The Coffee
                </a>
              </li>
              <li>
                <a href="#product" className="link-wave" data-testid="footer-link-shop">
                  Shop
                </a>
              </li>
              <li>
                <a href="#faq" className="link-wave" data-testid="footer-link-faq">
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--fs-gold-muted)] mb-4">
              Stay in the flow
            </div>
            <p className="text-sm text-neutral-400 mb-5 max-w-sm leading-relaxed">
              Occasional dispatches on roast drops, brewing, and focus.
            </p>
            <form
              className="flex items-center gap-2 border border-[color:var(--fs-hairline)] rounded-full pl-5 pr-1 py-1 max-w-sm"
              onSubmit={(e) => e.preventDefault()}
              data-testid="footer-newsletter-form"
            >
              <Mail size={14} className="text-[color:var(--fs-gold-muted)]" />
              <input
                type="email"
                placeholder="your@email.com"
                aria-label="Email"
                className="flex-1 bg-transparent outline-none text-sm py-2 text-neutral-200 placeholder:text-neutral-600"
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                className="btn-ghost-gold !py-2 !px-4 !text-[10px]"
                data-testid="footer-newsletter-submit"
              >
                Join
              </button>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] tracking-[0.2em] uppercase text-neutral-600">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} Flow State Coffee. Roasted in the UAE.
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 link-wave"
              data-testid="footer-instagram"
            >
              <Instagram size={13} /> Instagram
            </a>
            <a href="#faq" className="link-wave" data-testid="footer-support">
              Support
            </a>
            <a href="#top" className="link-wave" data-testid="footer-privacy">
              Privacy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
